import type { SnapshotTrace } from '../lib/telemetry/snapshotTrace';
/**
 * #1124 — the Data pressure card's samplers.
 *
 * Venues: one module-level sampler, shared by every mounted card, reads the
 * feed counters once a second (visibility-aware: every 6th second while the
 * tab is hidden, with the 60 s peak frozen). Connections: a separate 2 s
 * sampler over the rows-free `getFeedPulls()`, running only while a card has
 * its Connections list open.
 *
 * Memory (Brendan, #836): everything here is fixed-size and torn down with
 * the last subscriber. Subscribers are React state setters, which are stable
 * and close over no render scope, so nothing a card renders is kept alive by
 * this module after it unmounts.
 */
import { useEffect, useState } from 'react';
import { getFeedHealth, getFeedPulls, type FeedHealthSnapshot, type FeedPullSnapshot } from '@/hooks/useHydra';
import { getTabLag } from '@/clients/HydraClient';
import { getMarketCountsBySport, getOrderbookNeedsByView, useMarketStore } from '@/stores/marketStore';
import { feedNeedsBySource } from '@/stores/feedNeeds';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';
import { SHOWN, SLOT, counterDelta, receivedCounts, shownCounts } from '@/lib/telemetry/feedCounters';
import { CONNECTION_EVENT_RING, connectionEventsSince } from '@/lib/telemetry/connectionEvents';
import { readLongTasks, startLongTasks, stopLongTasks } from '@/lib/telemetry/longTasks';
import { readPageMemory, resetPageMemoryRing, samplePageMemory, type PageMemoryReading } from '@/lib/telemetry/pageMemory';
import { groupMarketViews, viewGroupOf, type MarketViewGroup } from '@/lib/telemetry/viewGroups';

export type FeedVenue = 'kalshi' | 'polyIntl' | 'polyUs' | 'odds';

export interface VenueRate {
  venue: FeedVenue;
  /** Null for odds (no books) and before the first measured second. */
  booksPerSec: number | null;
  marketsPerSec: number | null;
  /** Share of received books that reached a view, 0-100. Kalshi and Poly INTL only. */
  shownPct: number | null;
  /** Highest books + markets in one second over the last 60 visible seconds. */
  peakPerSec: number | null;
  /** Odds only: the Unabated / OddsPapi split of marketsPerSec. */
  unabatedPerSec?: number | null;
  oddspapiPerSec?: number | null;
}

/** #1124 B1: what this tab holds and which views are asking for it. */
export interface HoldingsReading {
  /** Views widening what the markets feed carries, grouped by name. */
  marketViews: readonly MarketViewGroup[];
  /** Books each view group asks for, largest first (a group's shared tickers count once). */
  bookViews: readonly { label: string; books: number }[];
  /** The union this tab routes (views sharing a ticker cost one book). */
  booksNeeded: number;
  /** What the fleet delivered under this tab's filter. */
  marketsDelivered: number;
  booksDelivered: number;
  /** Top 3 sports by markets delivered (a market can count under two). */
  marketsBySport: readonly { sport: string; markets: number }[];
  /** g1 windows attached to this browser's busiest-shared feed worker; null in direct transport. */
  windows: number | null;
  /** Books routed to each connection, largest first. */
  booksByConnection: readonly { label: string; books: number }[];
}

/** #1124 B1: connection resets this tab saw and resnapshots it requested. */
export interface ChurnReading {
  snapshotTraces?: readonly { label: string; events: readonly SnapshotTrace[] }[];
  resets: number;
  resetsIn5m: number;
  /** Of the resets, the upstream socket itself coming back. */
  upstream: number;
  resnapshots: number;
  resnapshotsIn5m: number;
  /** The 64-event ring was full inside 5 min: the in-5-min counts are floors. */
  resetsIn5mCapped: boolean;
  resnapshotsIn5mCapped: boolean;
  /** Up to 3 connections with the most resets. */
  topResets: readonly { label: string; resets: number }[];
}

/** #1124 B1: connections hydra has reported backpressure on. The V1.0 fleet
 *  sends level 1 ("books coalesced") once per socket and no all-clear. */
export interface CoalescedReading {
  connections: number;
  total: number;
  firstSinceMs: number | null;
  /** A connection reported the legacy level 2 ("books paused"). */
  paused: boolean;
  rows: readonly { id: string; label: string; level: number; sinceMs: number | null }[];
}

export interface FeedPressureSample {
  venues: readonly VenueRate[];
  /** Tab behind the feed worker, ms (a 5 s sample); null when no fresh sample. */
  lagMs: number | null;
  holdings: HoldingsReading | null;
  /** Null where the browser has no performance.memory. */
  memory: PageMemoryReading | null;
  /** Null while hidden, where unsupported, and before the first tick. */
  longTasks: { blockedMs: number; count: number } | null;
  churn: ChurnReading | null;
  coalesced: CoalescedReading | null;
  /** The tab is hidden: rates still update, the peak is frozen. */
  hidden: boolean;
  /** Wall clock of the sample, for the report; null before the first second. */
  sampledAtMs: number | null;
}

const VENUES: readonly FeedVenue[] = ['kalshi', 'polyIntl', 'polyUs', 'odds'];
const PEAK_WINDOW = 60;
/** Shortest sample, in seconds, that may enter the peak ring. */
const MIN_PEAK_SPAN_S = 0.5;
const SAMPLE_MS = 1000;
/** Chrome's heap reading can't change faster than this (it refreshes every ~20 min). */
const MEMORY_EVERY_TICKS = 10;
const CHURN_WINDOW_MS = 5 * 60_000;
const CONNECTIONS_MS = 2000;

const EMPTY: FeedPressureSample = {
  venues: VENUES.map((venue) => ({
    venue, booksPerSec: null, marketsPerSec: null, shownPct: null, peakPerSec: null,
    ...(venue === 'odds' ? { unabatedPerSec: null, oddspapiPerSec: null } : {}),
  })),
  lagMs: null,
  holdings: null,
  memory: null,
  longTasks: null,
  churn: null,
  coalesced: null,
  hidden: false,
  sampledAtMs: null,
};

// Fixed-size state, allocated once.
const prevReceived = new Uint32Array(receivedCounts.length);
const prevShown = new Uint32Array(shownCounts.length);
const peakRings = VENUES.map(() => new Float64Array(PEAK_WINDOW));
let ringNext = 0;
let ringFilled = 0;
let prevT: number | null = null;
let latest: FeedPressureSample = EMPTY;
let stopTimer: (() => void) | null = null;
let tickCount = 0;

// The header says "Paused while tab hidden" the moment the tab hides, not at
// the next hidden tick (up to 6 s later). Returning ticks at once already.
function onVisibility(): void {
  if (!isHidden() || latest.hidden) return;
  latest = { ...latest, hidden: true };
  for (const fn of subscribers) fn(latest);
}
const subscribers = new Set<(s: FeedPressureSample) => void>();

function isHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden';
}

function baseline(): void {
  prevReceived.set(receivedCounts);
  prevShown.set(shownCounts);
  prevT = performance.now();
}

/** Null when nothing arrived, and for a second where shown exceeds received:
 *  a worker replay isn't counted received but its needed books count shown,
 *  so the share for that second isn't known. */
function pct(shown: number, received: number): number | null {
  return received > 0 && shown <= received ? (shown / received) * 100 : null;
}

function peakOf(ring: Float64Array): number | null {
  if (ringFilled === 0) return null;
  let max = 0;
  for (let i = 0; i < ringFilled; i += 1) if (ring[i] > max) max = ring[i];
  return max;
}

function holdingsOf(health: readonly FeedHealthSnapshot[]): HoldingsReading {
  const needs = getOrderbookNeedsByView(viewGroupOf);
  const state = useMarketStore.getState();
  let windows: number | null = null;
  for (const h of health) if (h.connected && h.windows != null && (windows == null || h.windows > windows)) windows = h.windows;
  return {
    marketViews: groupMarketViews(feedNeedsBySource()),
    bookViews: needs.views.map((v) => ({ label: v.viewId, books: v.books })).sort((a, b) => b.books - a.books),
    booksNeeded: needs.total,
    marketsDelivered: state.marketsCount,
    booksDelivered: state.orderbooksCount,
    marketsBySport: getMarketCountsBySport().slice(0, 3),
    windows,
    booksByConnection: health.filter((h) => h.booksRouted > 0)
      .map((h) => ({ label: h.label ?? h.id, books: h.booksRouted }))
      .sort((a, b) => b.books - a.books),
  };
}

function churnOf(health: readonly FeedHealthSnapshot[], now: number): ChurnReading {
  let resets = 0;
  let upstream = 0;
  let resnapshots = 0;
  for (const h of health) {
    resets += h.connectionResets;
    upstream += h.upstreamReconnects;
    resnapshots += h.resnapshotRequests;
  }
  const resetsIn5m = connectionEventsSince('reset', now - CHURN_WINDOW_MS);
  const resnapshotsIn5m = connectionEventsSince('resnapshot', now - CHURN_WINDOW_MS);
  return {
    resets, resetsIn5m, upstream, resnapshots, resnapshotsIn5m,
    snapshotTraces: health.filter(h => h.snapshotTrace?.length).map(h => ({ label: h.label ?? h.id, events: h.snapshotTrace! })),
    resetsIn5mCapped: resetsIn5m >= CONNECTION_EVENT_RING,
    resnapshotsIn5mCapped: resnapshotsIn5m >= CONNECTION_EVENT_RING,
    topResets: health.filter((h) => h.connectionResets > 0)
      .map((h) => ({ label: h.label ?? h.id, resets: h.connectionResets }))
      .sort((a, b) => b.resets - a.resets)
      .slice(0, 3),
  };
}

function coalescedOf(health: readonly FeedHealthSnapshot[]): CoalescedReading {
  const rows = health.filter((h) => h.backpressureLevel >= 1)
    .map((h) => ({ id: h.id, label: h.label ?? h.id, level: h.backpressureLevel, sinceMs: h.backpressureSinceMs }))
    .sort((a, b) => (a.sinceMs ?? Infinity) - (b.sinceMs ?? Infinity));
  return {
    connections: rows.length,
    total: health.length,
    firstSinceMs: rows[0]?.sinceMs ?? null,
    paused: rows.some((r) => r.level >= 2),
    rows,
  };
}

function tick(): void {
  const now = performance.now();
  if (prevT == null) { baseline(); return; }
  const dt = (now - prevT) / 1000;
  if (dt <= 0) return;
  const d = (slot: number) => counterDelta(receivedCounts[slot], prevReceived[slot]);
  const ds = (slot: number) => counterDelta(shownCounts[slot], prevShown[slot]);
  const rate = (n: number) => n / dt;

  const kB = d(SLOT.kalshiBook), kM = d(SLOT.kalshiMarket);
  const iB = d(SLOT.polyIntlBook), iM = d(SLOT.polyIntlMarket);
  const uB = d(SLOT.polyUsBook), uM = d(SLOT.polyUsMarket);
  const oU = d(SLOT.oddsUnabated), oP = d(SLOT.oddsOddspapi);
  const totals = [kB + kM, iB + iM, uB + uM, oU + oP].map(rate);

  const hidden = isHidden();
  // The tick on return to the tab, and the interval tick after it, can span
  // far less than a second; a burst over that span is not a per-second peak.
  if (!hidden && dt >= MIN_PEAK_SPAN_S) {
    for (let v = 0; v < VENUES.length; v += 1) peakRings[v][ringNext] = totals[v];
    ringNext = (ringNext + 1) % PEAK_WINDOW;
    ringFilled = Math.min(PEAK_WINDOW, ringFilled + 1);
  }

  tickCount += 1;
  if (tickCount % MEMORY_EVERY_TICKS === 1) samplePageMemory();
  const health = getFeedHealth();

  latest = {
    venues: [
      { venue: 'kalshi', booksPerSec: rate(kB), marketsPerSec: rate(kM), shownPct: pct(ds(SHOWN.kalshi), kB), peakPerSec: peakOf(peakRings[0]) },
      { venue: 'polyIntl', booksPerSec: rate(iB), marketsPerSec: rate(iM), shownPct: pct(ds(SHOWN.polyIntl), iB), peakPerSec: peakOf(peakRings[1]) },
      { venue: 'polyUs', booksPerSec: rate(uB), marketsPerSec: rate(uM), shownPct: null, peakPerSec: peakOf(peakRings[2]) },
      { venue: 'odds', booksPerSec: null, marketsPerSec: rate(oU + oP), shownPct: null, peakPerSec: peakOf(peakRings[3]), unabatedPerSec: rate(oU), oddspapiPerSec: rate(oP) },
    ],
    lagMs: getTabLag()?.lagMs ?? null,
    holdings: holdingsOf(health),
    memory: readPageMemory(),
    // Background throttling makes long tasks meaningless while hidden.
    longTasks: hidden ? null : readLongTasks(),
    churn: churnOf(health, now),
    coalesced: coalescedOf(health),
    hidden,
    sampledAtMs: Date.now(),
  };
  baseline();
  for (const fn of subscribers) fn(latest);
}

function start(): void {
  latest = EMPTY;
  ringNext = 0;
  ringFilled = 0;
  for (const ring of peakRings) ring.fill(0);
  tickCount = 0;
  baseline();
  startLongTasks();
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
  stopTimer = startVisibilityAwareInterval(tick, SAMPLE_MS);
}

function stop(): void {
  stopTimer?.();
  stopTimer = null;
  if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
  stopLongTasks();
  resetPageMemoryRing();
  prevT = null;
  latest = EMPTY;
}

function subscribe(fn: (s: FeedPressureSample) => void): () => void {
  subscribers.add(fn);
  if (subscribers.size === 1) start();
  return () => {
    subscribers.delete(fn);
    if (subscribers.size === 0) stop();
  };
}

/** Venue rates for a mounted card. The state lives in the calling component. */
export function useFeedPressure(): FeedPressureSample {
  const [sample, setSample] = useState<FeedPressureSample>(latest);
  useEffect(() => subscribe(setSample), []);
  return sample;
}

export interface ConnectionRow {
  id: string;
  label: string;
  framesPerSec: number;
  bytesPerSec: number;
  tabs: number;
  /** Hydra's backpressure on this socket; 1 = books coalesced (sticky). */
  backpressureLevel: number;
  backpressureSinceMs: number | null;
  /** How this socket reaches the tab: through the shared feed worker, or direct. */
  transport: FeedPullSnapshot['transport'];
  /** The span the rates cover (see FeedPullSnapshot.pull.windowMs). */
  windowMs: number | null;
}

export interface FeedConnections {
  /** Connected sockets that delivered frames in the worker's last window, busiest first. */
  rows: readonly ConnectionRow[];
  /** Connected, but no frames in the last window. */
  idle: number;
  /** Connected, but no window measured yet (its first 5 s). */
  starting: number;
  /** Not connected right now. */
  reconnecting: number;
  total: number;
}

function summarize(pulls: readonly FeedPullSnapshot[]): FeedConnections {
  const rows: ConnectionRow[] = [];
  let idle = 0;
  let starting = 0;
  let reconnecting = 0;
  for (const p of pulls) {
    if (!p.connected) { reconnecting += 1; continue; }
    if (!p.pull) { starting += 1; continue; }
    if (p.pull.framesPerSec <= 0) { idle += 1; continue; }
    rows.push({
      id: p.id, label: p.label ?? p.id, framesPerSec: p.pull.framesPerSec, bytesPerSec: p.pull.bytesPerSec, tabs: p.pull.tabs,
      backpressureLevel: p.backpressureLevel, backpressureSinceMs: p.backpressureSinceMs,
      transport: p.transport, windowMs: p.pull.windowMs ?? null,
    });
  }
  rows.sort((a, b) => b.framesPerSec - a.framesPerSec);
  return { rows, idle, starting, reconnecting, total: pulls.length };
}

/** #1124: one rows-free read of the connections, for a Copy report taken
 *  with the list closed. No interval and nothing kept. In direct mode this
 *  rolls each direct socket's window, so the rows carry the span it covered. */
export function readConnectionsNow(): FeedConnections {
  return summarize(getFeedPulls());
}

/** Per-connection rows, sampled every 2 s only while `open`. */
export function useFeedConnections(open: boolean): FeedConnections | null {
  const [connections, setConnections] = useState<FeedConnections | null>(null);
  useEffect(() => {
    if (!open) return;
    const read = () => setConnections(summarize(getFeedPulls()));
    const first = setTimeout(read, 0);
    const stopInterval = startVisibilityAwareInterval(read, CONNECTIONS_MS);
    return () => {
      clearTimeout(first);
      stopInterval();
      setConnections(null);
    };
  }, [open]);
  return open ? connections : null;
}

/** For the leak test: how many cards hold the venue sampler. */
export function feedPressureSubscriberCount(): number {
  return subscribers.size;
}

export function __resetFeedPressureForTests(): void {
  subscribers.clear();
  stop();
}
